(() => {
  if (window.AWAnalytics?.version) return;

  const PREFIX = 'assassin_workshop:';
  const memory = new Set();
  const pending = { compatibility: null };
  const debug = new URLSearchParams(window.location.search).get('aw_analytics_debug') === '1';
  const themeRole = String(window.Shopify?.theme?.role || '').toLowerCase();
  const isPreview = Boolean(
    window.Shopify?.designMode ||
    (themeRole && themeRole !== 'main') ||
    /(?:\?|&)preview_theme_id=/.test(window.location.search) ||
    /(?:\?|&)previewPath=/.test(window.location.search)
  );

  const normalize = (value = '') => String(value)
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '');

  const safePrimitive = (value) => {
    if (value === null || value === undefined) return undefined;
    if (typeof value === 'boolean' || typeof value === 'number') return value;
    if (typeof value === 'string') return value.slice(0, 180);
    if (Array.isArray(value)) return value.slice(0, 12).map(safePrimitive).filter((item) => item !== undefined);
    return undefined;
  };

  const sanitize = (payload = {}) => Object.entries(payload).reduce((acc, [key, value]) => {
    const safeKey = normalize(key).slice(0, 60);
    const safeValue = safePrimitive(value);
    if (safeKey && safeValue !== undefined) acc[safeKey] = safeValue;
    return acc;
  }, {});

  const common = () => ({
    schema_version: 1,
    path: window.location.pathname.slice(0, 180),
    locale: document.documentElement.lang || '',
    currency: window.Shopify?.currency?.active || '',
    preview_mode: isPreview
  });

  const track = (name, payload = {}) => {
    const event = normalize(name);
    if (!event) return;
    const data = { ...common(), ...sanitize(payload) };
    const eventName = `${PREFIX}${event}`;

    window.dispatchEvent(new CustomEvent('aw:analytics', {
      detail: { event: eventName, payload: data, suppressed: isPreview }
    }));

    if (debug) console.info('[AW Analytics]', eventName, data, isPreview ? '(preview — non publié)' : '');
    if (isPreview) return;

    const publishShopify = (attempt = 0) => {
      try {
        if (window.Shopify?.analytics?.publish) {
          window.Shopify.analytics.publish(eventName, data);
          return;
        }
      } catch (error) {
        if (debug) console.warn('[AW Analytics] Shopify publish failed', error);
        return;
      }
      if (attempt < 12) window.setTimeout(() => publishShopify(attempt + 1), 250);
    };
    publishShopify();

    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: `aw_${event}`, ...data });
    } catch (_) {}
  };

  const trackOnce = (key, name, payload = {}) => {
    const token = `${key}`;
    if (memory.has(token)) return;
    memory.add(token);
    track(name, payload);
  };

  const productHandleFromPath = () => {
    const match = window.location.pathname.match(/\/products\/([^/?#]+)/i);
    return match ? match[1] : '';
  };

  const resultState = (root) => {
    const result = root.querySelector('[data-aw-result]');
    if (!result) return 'unknown';
    const match = [...result.classList].find((item) => item.startsWith('aw-compatibility__result--'));
    return match ? match.replace('aw-compatibility__result--', '').replace(/-/g, '_') : 'unknown';
  };

  const compatibilitySnapshot = (root, productData = {}) => {
    const platform = root.querySelector('[data-aw-platform-select]');
    const manufacturer = root.querySelector('[data-aw-manufacturer-select]');
    const replicaType = root.querySelector('[data-aw-replica-type-select]');
    const scoreText = root.querySelector('[data-aw-score-value]')?.textContent || '';
    return {
      product_handle: productHandleFromPath(),
      selected_platform: platform?.value || '',
      selected_manufacturer: manufacturer?.value || '',
      selected_replica_type: replicaType?.value || '',
      result: resultState(root),
      score: Number.parseInt(scoreText, 10) || 0,
      platform_data_count: Array.isArray(productData.platforms) ? productData.platforms.length : 0,
      manufacturer_data_count: Array.isArray(productData.manufacturers) ? productData.manufacturers.length : 0,
      replica_type_data_count: Array.isArray(productData.replicaTypes) ? productData.replicaTypes.length : 0,
      workshop_recommended: productData.workshopInstallation === true,
      installation_level: productData.installationLevel || ''
    };
  };

  const initCompatibility = (root) => {
    if (!root || root.dataset.awAnalyticsReady === 'true') return;
    const dataNode = root.querySelector('[data-aw-product-compatibility]');
    let productData = {};
    try { productData = JSON.parse(dataNode?.textContent || '{}'); } catch (_) {}

    const base = compatibilitySnapshot(root, productData);
    trackOnce(`compatibility_viewed:${base.product_handle}`, 'compatibility_viewed', {
      product_handle: base.product_handle,
      has_platform_data: base.platform_data_count > 0,
      platform_data_count: base.platform_data_count,
      manufacturer_data_count: base.manufacturer_data_count,
      replica_type_data_count: base.replica_type_data_count,
      workshop_recommended: base.workshop_recommended,
      installation_level: base.installation_level
    });

    let interacted = false;
    let lastCheck = null;
    const capture = (source) => window.setTimeout(() => {
      const snapshot = compatibilitySnapshot(root, productData);
      if (!snapshot.selected_platform) return;
      interacted = true;
      lastCheck = snapshot;
      track('compatibility_checked', { ...snapshot, interaction_source: source });
      if (snapshot.workshop_recommended) {
        trackOnce(`workshop_recommendation:${snapshot.product_handle}`, 'workshop_recommendation_exposed', {
          product_handle: snapshot.product_handle,
          compatibility_result: snapshot.result,
          compatibility_score: snapshot.score,
          installation_level: snapshot.installation_level
        });
      }
    }, 0);

    root.addEventListener('click', (event) => {
      if (event.target.closest('[data-aw-platform-button]')) capture('platform_button');
    });
    root.addEventListener('change', (event) => {
      if (event.target.matches('[data-aw-platform-select]')) capture('platform_select');
      if (event.target.matches('[data-aw-manufacturer-select]')) capture('manufacturer_select');
      if (event.target.matches('[data-aw-replica-type-select]')) capture('replica_type_select');
    });

    document.addEventListener('submit', (event) => {
      if (!interacted || !lastCheck) return;
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || !String(form.action || '').includes('/cart/add')) return;
      const variantId = form.querySelector('[name="id"]')?.value || '';
      pending.compatibility = { ...lastCheck, variant_id: variantId, time: Date.now() };
      track('compatibility_add_to_cart_intent', { ...lastCheck, variant_id: variantId });
    }, true);

    root.dataset.awAnalyticsReady = 'true';
  };

  document.addEventListener('cart:updated', () => {
    const now = Date.now();
    if (pending.compatibility && now - pending.compatibility.time < 15000) {
      track('compatibility_add_to_cart_confirmed', pending.compatibility);
      pending.compatibility = null;
    }
  });

  const initAll = (scope = document) => {
    scope.querySelectorAll?.('[data-aw-compatibility]').forEach(initCompatibility);
  };

  window.AWAnalytics = {
    version: '1.1.0',
    track,
    trackOnce,
    preview: isPreview,
    events: {
      compatibilityUsage: ['compatibility_viewed', 'compatibility_checked'],
      compatibilityToCart: ['compatibility_checked', 'compatibility_add_to_cart_confirmed'],
      workshopOpportunity: ['workshop_recommendation_exposed']
    }
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  else initAll();
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();

/* Attribution V2 */
(() => {
  if (window.AWAttribution?.version) return;

  const SCHEMA = '2';
  const ATTRIBUTION_KEY = 'aw_cart_attribution_v2';
  const CART_STATE_KEY = 'aw_cart_measurement_v2';
  const pending = { compatibility: null, configurator: null };
  const config = window.AWAnalyticsConfig || {};
  const workshopHandles = new Set((config.workshopHandles || []).map(String));
  const nativeFetch = window.fetch?.bind(window);

  const readJson = (storage, key, fallback = {}) => {
    try {
      const value = JSON.parse(storage.getItem(key) || 'null');
      return value ?? fallback;
    } catch (_) {
      return fallback;
    }
  };

  const writeJson = (storage, key, value) => {
    try { storage.setItem(key, JSON.stringify(value)); } catch (_) {}
  };

  const track = (name, payload = {}) => {
    try { window.AWAnalytics?.track?.(name, payload); } catch (_) {}
  };

  const resultState = (root) => {
    const result = root?.querySelector('[data-aw-result]');
    const match = result ? [...result.classList].find((item) => item.startsWith('aw-compatibility__result--')) : '';
    return match ? match.replace('aw-compatibility__result--', '').replace(/-/g, '_') : '';
  };

  const compatibilitySnapshot = (root) => {
    if (!root) return null;
    const dataNode = root.querySelector('[data-aw-product-compatibility]');
    let productData = {};
    try { productData = JSON.parse(dataNode?.textContent || '{}'); } catch (_) {}
    const score = Number.parseInt(root.querySelector('[data-aw-score-value]')?.textContent || '', 10) || 0;
    const platform = root.querySelector('[data-aw-platform-select]')?.value || '';
    if (!platform) return null;
    return {
      source: 'compatibility',
      platform,
      manufacturer: root.querySelector('[data-aw-manufacturer-select]')?.value || '',
      replica_type: root.querySelector('[data-aw-replica-type-select]')?.value || '',
      result: resultState(root),
      score,
      workshop_recommended: productData.workshopInstallation === true,
      installation_level: productData.installationLevel || '',
      time: Date.now()
    };
  };

  const configuratorSnapshot = (root) => {
    if (!root) return null;
    const state = readJson(localStorage, root.dataset.storageKey || 'aw_airsoft_configurator', {});
    if (!state.platform) return null;
    return {
      source: 'configurator',
      configuration_name: state.name || '',
      platform: state.platform || '',
      manufacturer: state.manufacturer || '',
      replica_type: state.type || '',
      goal: state.goal || '',
      selected_count: Array.isArray(state.selected) ? state.selected.length : 0,
      time: Date.now()
    };
  };

  const isFresh = (entry) => Boolean(entry && Date.now() - Number(entry.time || 0) < 20000);

  const currentAttribution = () => {
    if (isFresh(pending.configurator)) return pending.configurator;
    if (isFresh(pending.compatibility)) return pending.compatibility;
    return null;
  };

  const propertiesFor = (entry = {}) => {
    const values = {
      '_AW Schema': SCHEMA,
      '_AW Source': entry.source || '',
      '_AW Preview': window.AWAnalytics?.preview ? 'true' : 'false',
      '_AW Platform': entry.platform || '',
      '_AW Manufacturer': entry.manufacturer || '',
      '_AW Replica Type': entry.replica_type || '',
      '_AW Configuration': entry.configuration_name || '',
      '_AW Goal': entry.goal || '',
      '_AW Compatibility Result': entry.result || '',
      '_AW Compatibility Score': entry.score === undefined ? '' : String(entry.score),
      '_AW Workshop Recommended': entry.workshop_recommended === true ? 'true' : (entry.workshop_recommended === false ? 'false' : ''),
      '_AW Installation Level': entry.installation_level || ''
    };
    return Object.fromEntries(
      Object.entries(values)
        .filter(([, value]) => value !== '')
        .map(([key, value]) => [key, String(value).slice(0, 240)])
    );
  };

  const decorateBody = (body, entry) => {
    const properties = propertiesFor(entry);
    if (!Object.keys(properties).length) return body;

    if (body instanceof FormData) {
      Object.entries(properties).forEach(([key, value]) => body.set(`properties[${key}]`, value));
      return body;
    }

    if (body instanceof URLSearchParams) {
      Object.entries(properties).forEach(([key, value]) => body.set(`properties[${key}]`, value));
      return body;
    }

    if (typeof body === 'string') {
      try {
        const parsed = JSON.parse(body);
        if (Array.isArray(parsed?.items)) {
          parsed.items = parsed.items.map((item) => ({
            ...item,
            properties: { ...(item.properties || {}), ...properties }
          }));
        } else if (parsed && typeof parsed === 'object') {
          parsed.properties = { ...(parsed.properties || {}), ...properties };
        }
        return JSON.stringify(parsed);
      } catch (_) {}
    }

    return body;
  };

  const remember = (entry) => {
    if (!entry) return;
    writeJson(sessionStorage, ATTRIBUTION_KEY, {
      source: entry.source || '',
      platform: entry.platform || '',
      workshop_recommended: entry.workshop_recommended === true,
      time: Date.now()
    });
  };

  const itemProperties = (item) => item?.properties && typeof item.properties === 'object' ? item.properties : {};

  const summarizeCart = (cart) => {
    const items = Array.isArray(cart?.items) ? cart.items : [];
    const attributed = items.filter((item) => {
      const props = itemProperties(item);
      return Boolean(props['_AW Source'] || props['Plateforme AW'] || props['Configuration AW']);
    });
    const sources = [...new Set(attributed.map((item) => itemProperties(item)['_AW Source']).filter(Boolean))];
    return {
      token: cart?.token || '',
      item_count: Number(cart?.item_count || 0),
      aw_attributed: attributed.length > 0,
      attributed_item_count: attributed.length,
      source: sources.join(','),
      workshop_recommended: attributed.some((item) => itemProperties(item)['_AW Workshop Recommended'] === 'true'),
      workshop_service_attached: items.some((item) => workshopHandles.has(String(item.handle || '')))
    };
  };

  let inspection = null;
  const inspectCart = (reason = 'update') => {
    if (!nativeFetch || inspection) return inspection || Promise.resolve(null);
    const root = window.Shopify?.routes?.root || '/';
    inspection = nativeFetch(`${root}cart.js`, { headers: { Accept: 'application/json' } })
      .then((response) => response.ok ? response.json() : null)
      .then((cart) => {
        if (!cart) return null;
        const state = summarizeCart(cart);
        const previous = readJson(sessionStorage, CART_STATE_KEY, {});
        writeJson(sessionStorage, CART_STATE_KEY, state);

        const changed = previous.token !== state.token ||
          previous.item_count !== state.item_count ||
          previous.attributed_item_count !== state.attributed_item_count ||
          previous.workshop_recommended !== state.workshop_recommended ||
          previous.workshop_service_attached !== state.workshop_service_attached;

        if (changed && state.aw_attributed) track('aw_cart_state', { ...state, inspection_reason: reason });
        if (state.workshop_recommended && state.workshop_service_attached && !previous.workshop_service_attached) {
          track('workshop_service_attached', state);
        }
        return state;
      })
      .catch(() => null)
      .finally(() => { inspection = null; });
    return inspection;
  };

  if (nativeFetch) {
    window.fetch = (input, init = {}) => {
      let attribution = null;
      let nextInit = init;
      let cartMutation = false;
      try {
        const url = typeof input === 'string' ? input : (input?.url || '');
        const method = String(init?.method || input?.method || 'GET').toUpperCase();
        const cartAdd = method === 'POST' && /\/cart\/add(?:\.js)?(?:[?#]|$)/i.test(url);
        cartMutation = method === 'POST' && /\/cart\/(?:add|update|change|clear)(?:\.js)?(?:[?#]|$)/i.test(url);
        if (cartAdd) {
          attribution = currentAttribution();
          if (attribution && Object.prototype.hasOwnProperty.call(init, 'body')) {
            nextInit = { ...init, body: decorateBody(init.body, attribution) };
          }
        }
      } catch (_) {}

      const request = nativeFetch(input, nextInit);
      const storedAttribution = readJson(sessionStorage, ATTRIBUTION_KEY, {});
      const storedCartState = readJson(sessionStorage, CART_STATE_KEY, {});
      const shouldInspect = cartMutation && Boolean(attribution || storedAttribution.source || storedCartState.aw_attributed);
      if (!attribution && !shouldInspect) return request;

      return request.then((response) => {
        if (response?.ok && attribution) {
          remember(attribution);
          if (attribution.source === 'compatibility') pending.compatibility = null;
          if (attribution.source === 'configurator') pending.configurator = null;
        }
        if (response?.ok && shouldInspect) {
          window.setTimeout(() => inspectCart(attribution ? 'attributed_add' : 'cart_mutation'), 80);
        }
        return response;
      });
    };
  }

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    if (String(form.action || '').includes('/cart/add')) {
      const compatibility = document.querySelector('[data-aw-compatibility]');
      const snapshot = compatibilitySnapshot(compatibility);
      if (snapshot) pending.compatibility = snapshot;
    }

    if (form.querySelector('[name="checkout"]') || form.classList.contains('cart-drawer__checkout-form')) {
      const state = readJson(sessionStorage, CART_STATE_KEY, {});
      const attribution = readJson(sessionStorage, ATTRIBUTION_KEY, {});
      track('checkout_intent', {
        aw_attributed: Boolean(state.aw_attributed),
        attributed_item_count: Number(state.attributed_item_count || 0),
        source: state.source || attribution.source || '',
        workshop_recommended: Boolean(state.workshop_recommended),
        workshop_service_attached: Boolean(state.workshop_service_attached)
      });
    }
  }, true);

  document.addEventListener('click', (event) => {
    const addAll = event.target.closest('[data-aw-config-add-all]');
    if (addAll) {
      const root = addAll.closest('[data-aw-configurator]');
      const snapshot = configuratorSnapshot(root);
      if (snapshot) pending.configurator = snapshot;
    }

    if (event.target.closest('a[href*="/checkout"]')) {
      const state = readJson(sessionStorage, CART_STATE_KEY, {});
      const attribution = readJson(sessionStorage, ATTRIBUTION_KEY, {});
      track('checkout_intent', {
        aw_attributed: Boolean(state.aw_attributed),
        attributed_item_count: Number(state.attributed_item_count || 0),
        source: state.source || attribution.source || '',
        workshop_recommended: Boolean(state.workshop_recommended),
        workshop_service_attached: Boolean(state.workshop_service_attached)
      });
    }
  }, true);

  document.addEventListener('cart:updated', () => inspectCart('cart_updated'));
  document.addEventListener('cart:page-updated', () => inspectCart('cart_page_updated'));

  window.AWAttribution = {
    version: '2.0.0',
    schema: SCHEMA,
    inspectCart,
    propertiesFor
  };
})();
